import { getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { CircleUserRound, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useERSContext, useMapContext } from '@/context';
import { useAIProvider } from '@/hooks/useAIProvider';
import { capitalizeWords, constants, getResponderBadge } from '@/lib';
import { Nodata, PTTIcon } from '..';
import { Badge } from '../Badge/Badge';
import { Searchbar } from '../Searchbar/Searchbar';
import { StatusTabs } from '../StatusTabs/StatusTabs';
import { RespondersListSkeleton } from '../loader';

export const RespondersList = ({ users, onRowClick }) => {
  const { userSpeaking, latestResponder, incidentAssigneeStatusList } = useERSContext();
  const { callTranscribeAudio } = useAIProvider({ temperature: 0.6 });
  const { selectedResponder, unFocusResponder } = useMapContext();
  const cardRef = useRef(null);
  const { t } = useTranslation();
  const statusIdToDisplayNameMap = (incidentAssigneeStatusList || []).reduce((acc, status) => {
    acc[status.id] = status.display_name;
    return acc;
  }, {});

  useEffect(() => {
    if (cardRef.current && selectedResponder) {
      cardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedResponder]);

  // Check if a user is speaking
  const isUserSpeaking = user => {
    return userSpeaking && userSpeaking[user.id] === true;
  };

  const [activePresenceTab, setActivePresenceTab] = useState(constants.TAB_KEYS.ALL);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);

  // PTT state
  const [activePttUser, setActivePttUser] = useState(null);
  const [isPttRecording, setIsPttRecording] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const table = useReactTable({
    data: latestResponder || [],
    columns: constants.RESPONDERS.LIST.COLUMNS,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',
  });

  // Available statuses - all connected states (exclude only offline/disconnected/unavailable)
  const availableStatuses = ['online', 'available', 'chat', 'away', 'dnd', 'busy', 'xa'];

  const tabCounts = useMemo(() => {
    const counts = {
      [constants.TAB_KEYS.ALL]: latestResponder?.length || 0,
      [constants.TAB_KEYS.AVAILABLE]: 0,
      [constants.TAB_KEYS.ACTIVE]: 0, // "Online"
      [constants.TAB_KEYS.OFFLINE]: 0,
    };

    const offlineStatuses = [
      constants.RESPONDERS?.LIST?.PRESENCE_STATUS?.OFFLINE,
      constants.RESPONDERS?.LIST?.PRESENCE_STATUS?.DISCONNECTED,
      constants.RESPONDERS?.LIST?.PRESENCE_STATUS?.UNAVAILABLE,
    ];

    latestResponder?.forEach(responder => {
      const status = responder.presence;

      if (availableStatuses.includes(status)) {
        counts[constants.TAB_KEYS.AVAILABLE]++;
      }

      if (status === constants.RESPONDERS.LIST.PRESENCE_STATUS.ONLINE) {
        counts[constants.TAB_KEYS.ACTIVE]++;
      }

      if (offlineStatuses.includes(status)) {
        counts[constants.TAB_KEYS.OFFLINE]++;
      }
    });

    return counts;
  }, [latestResponder]);

  const handleTabChange = tabKey => {
    setActivePresenceTab(tabKey);

    const presenceColumn = table.getColumn('presence');

    switch (tabKey) {
      case constants?.TAB_KEYS?.ALL:
        presenceColumn?.setFilterValue(undefined);
        break;

      case constants?.TAB_KEYS?.ACTIVE:
        presenceColumn?.setFilterValue(constants?.RESPONDERS?.LIST.PRESENCE_STATUS?.ONLINE);
        break;

      case constants?.TAB_KEYS?.AVAILABLE:
        presenceColumn?.setFilterValue(availableStatuses);
        break;

      case constants?.TAB_KEYS?.OFFLINE:
        presenceColumn?.setFilterValue([
          constants.RESPONDERS?.LIST?.PRESENCE_STATUS?.OFFLINE,
          constants.RESPONDERS?.LIST?.PRESENCE_STATUS?.DISCONNECTED,
          constants.RESPONDERS?.LIST?.PRESENCE_STATUS?.UNAVAILABLE,
        ]);
        break;
      default:
        break;
    }
  };

  // PTT Functions - Using only SDK audio
  const startPtt = async user => {
    if (!user || isPttRecording) return;

    try {
      setActivePttUser(user);
      setIsPttRecording(true);

      // Start PTT session in SDK
      const incidentCall = await wbSdk.sipManager.openPtt('user', user.id, user.displayName, false);
    } catch (error) {
      setIsPttRecording(false);
      setActivePttUser(null);
    }
  };

  const stopPtt = async () => {
    if (!isPttRecording || !activePttUser) return;

    try {
      setIsPttRecording(false);

      // Close PTT session in SDK and get audio data
      const audioData = await wbSdk.sipManager.closePtt(activePttUser.id);

      // Send audio only if SDK returns it
      if (audioData && audioData.blob) {
        await handleSendPttAudio(audioData.blob, activePttUser);
      } else {
      }

      setActivePttUser(null);
    } catch (error) {
      setIsPttRecording(false);
      setActivePttUser(null);
    }
  };

  const handleSendPttAudio = async (audioBlob, user) => {
    try {
      if (!user || !audioBlob) return;

      let transcription = '';

      try {
        transcription = await transcribeAudio(audioBlob);
      } catch (transcriptionError) {}

      // Get chat box for the user (assuming direct message)
      const chatBox = await wbSdk.chatManager.getChatBox(user.id, false);

      const audioFile = new File(
        [audioBlob],
        `${constants.PTT.AUDIO.FILENAME_PREFIX}${Date.now()}${constants.PTT.AUDIO.FILE_EXTENSION}`,
        {
          type: constants.PTT.AUDIO.FILE_TYPE,
        }
      );

      await chatBox.sendMessage(transcription, [audioFile]);
    } catch (error) {
      console.error('Error sending PTT audio:', error);
    }
  };

  const transcribeAudio = async audioBlob => {
    try {
      const audioFile = new File([audioBlob], constants.PTT.AUDIO.FILE_EXTENSION, {
        type: constants.PTT.AUDIO.FILE_TYPE,
      });

      const response = await callTranscribeAudio(audioFile, {
        model: constants.PTT.TRANSCRIPTION.MODEL,
        language: constants.PTT.TRANSCRIPTION.LANGUAGE,
        response_format: constants.PTT.TRANSCRIPTION.RESPONSE_FORMAT,
      });

      return response;
    } catch (error) {
      console.error('OpenAI transcription error:', error);
      throw error;
    }
  };

  // PTT Icon click handler
  const handlePttClick = (user, event) => {
    event.stopPropagation(); // Prevent row click when clicking PTT icon

    if (isPttRecording && activePttUser?.id === user.id) {
      stopPtt();
    } else if (!isPttRecording) {
      startPtt(user);
    }
  };

  // Check if PTT is active for a specific user
  const isPttActiveForUser = user => {
    return isPttRecording && activePttUser?.id === user.id;
  };

  const getPresenceColor = presence => {
    const statusColors = constants.RESPONDERS.LIST.STATUS_COLORS;

    if (presence === constants.RESPONDERS.LIST.PRESENCE_STATUS.ONLINE) {
      return statusColors.ONLINE;
    } else if (presence === constants.RESPONDERS.LIST.PRESENCE_STATUS.OFFLINE) {
      return statusColors.OFFLINE;
    }
    return statusColors.DISCONNECTED;
  };

  const getNoDataDescription = () => {
    if (activePresenceTab === constants.TAB_KEYS.ALL) {
      return t('TAB_KEYS.ALL');
    }
    return t('FALLBACK_SCREEN.NO_AVAILABLE_RESPONDER_FOUND');
  };

  if (showSkeleton) {
    return <RespondersListSkeleton />;
  }

  if (!latestResponder || latestResponder.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Nodata
          title={t('FALLBACK_SCREEN.DATA_FOUND')}
          description={getNoDataDescription()}
          icon={<CircleUserRound size={120} absoluteStrokeWidth />}
          showButton={false}
        />
      </div>
    );
  }

  const tabs = [
    { key: constants.TAB_KEYS.ALL, label: capitalizeWords(t('TAB_KEYS.ALL')) },
    { key: constants.TAB_KEYS.AVAILABLE, label: capitalizeWords(t('TAB_KEYS.AVAILABLE')) },
  ];

  return (
    <div className="flex h-full flex-col bg overflow-hidden">
      <div className="p-3">
        <StatusTabs tabs={tabs} activeTab={activePresenceTab} onTabChange={handleTabChange} dataCount={tabCounts} />
      </div>

      <div className="px-3 pb-3">
        <Searchbar value={globalFilter} onChange={val => setGlobalFilter(val)} />
      </div>

      <div className="flex-1 overflow-y-auto px-3">
        {table.getRowModel().rows.length > 0 ? (
          <div
            className={clsx(
              'grid gap-2 text-xs',
              table.getRowCount() > 2
                ? constants.RESPONDERS.LIST.GRID_CONFIG.MULTIPLE_ROWS
                : constants.RESPONDERS.LIST.GRID_CONFIG.FEW_ROWS
            )}
          >
            {table.getRowModel().rows.map(row => {
              const rowData = row.original;
              const isUserPttActive = isPttActiveForUser(rowData);
              const isUserCurrentlySpeaking = isUserSpeaking(rowData);
              let isSelected = selectedResponder && row.original?.id === selectedResponder.id;

              return (
                <div
                  ref={cardRef}
                  key={row.id}
                  onClick={() => {
                    if (isSelected) {
                      isSelected = null;
                      unFocusResponder();
                    } else if (onRowClick) onRowClick(row.original?.users);
                  }}
                  className={clsx(
                    'flex flex-col justify-between p-3 border rounded-xl shadow-sm bg cursor-pointer',
                    isSelected ? 'border-interactive-active' : 'border-border'
                  )}
                >
                  <div ref={cardRef} className="flex flex-col gap-3">
                    <div className="flex flex-row items-center justify-between">
                      <div className="flex flex-row items-center gap-2">
                        <div className="relative">
                          <CircleUserRound size={32} absoluteStrokeWidth />
                          <span
                            className={clsx(
                              'absolute bottom-0.5 right-0.5 block h-2.5 w-2.5 rounded-full border-2 border-onColor',
                              getPresenceColor(rowData.presence)
                            )}
                          />
                        </div>
                        <div className="flex flex-col">
                          <span>{rowData?.displayName}</span>

                          {isUserCurrentlySpeaking ? (
                            <span className="text-xs text-interactive">{constants.CHAT_PANEL.IS_TALKING}</span>
                          ) : (
                            <span>{rowData?.presence == 'dnd' ? <VolumeX size={16} /> : <Volume2 size={16} />}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col justify-between">
                        <PTTIcon
                          isDisabled={false}
                          isActive={isUserPttActive}
                          onClick={e => handlePttClick(rowData, e)}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 border-t pt-2 border-border/50">
                      {(() => {
                        // Filter to only show incidents where the responder has interacted in the last 24 hours
                        const twentyFourHoursAgo = Math.floor(Date.now() / 1000) - 24 * 60 * 60;

                        const recentAssignments = rowData.allAssignments?.filter(assignment => {
                          // Check if the responder has any status record within the last 24 hours
                          return assignment.statusHistory?.some(
                            record => record.timestamp && record.timestamp >= twentyFourHoursAgo
                          );
                        });

                        if (recentAssignments?.length > 0) {
                          return recentAssignments.map((assignment, idx) => {
                            const badge = getResponderBadge(
                              {
                                assignee: { ...assignment.assigneeRecord },
                                statuses: assignment.statusHistory.map(d => d.status),
                              },
                              statusIdToDisplayNameMap
                            );

                            return (
                              <div key={assignment.incidentId || idx} className="flex flex-col gap-1">
                                <div
                                  className={clsx(
                                    'text-[11px] font-semibold',
                                    assignment.incidentStatus === 'resolved' && 'opacity-60'
                                  )}
                                >
                                  {assignment.incidentName}
                                  {assignment.incidentStatus === 'resolved' && (
                                    <span className="ml-1 text-[10px] font-normal italic">(Resolved)</span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between">
                                  <Badge variant={badge.variant} size="sm" className="text-[10px] py-0">
                                    {t('RESPONDER_STATUS', { returnObjects: true }).find(
                                      status => status.value === badge.label
                                    )?.label || badge.label}
                                  </Badge>
                                  <span className="text-[10px] text-muted-foreground italic px-1">
                                    {assignment.decision}
                                  </span>
                                </div>
                              </div>
                            );
                          });
                        }

                        return <div className="text-xs text-muted-foreground">{constants.UI.DASH}</div>;
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <Nodata
              title={t('MAP_VIEW.INCIDENT_TOOLTIP.RESPONDERS')}
              description={getNoDataDescription()}
              icon={<CircleUserRound size={120} absoluteStrokeWidth />}
              showButton={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};
